import { AppState } from '@app/core/state/appState';
import { CollaborationPackageActions } from '@app/core/state/collaboration-packages/collaboration-packages.actions';
import { ActionStrategy, ActionStrategyParams, ActionType } from '@app/core/state/types/collaboration-package.types';
import { Store } from '@ngrx/store';
import { formatSocialSecurityNumberWithCentry } from '@app/core/misc/validation-helper';
import { AppInjector } from 'src/app/app.injector';

const onboardingStrategy = (store: Store<AppState>): ActionStrategy => ({
  type: ActionType.ONBOARD,
  primaryButtonI18nKey: 'shared.select',
  execute: (config: ActionStrategyParams) => {
    const { cloudApiKey, clientId, email, subscription } = config;
    const loadRemoteVatIntervalInformation = !(subscription?.modules?.length > 0);

    if (clientId) {
      store.dispatch(
        CollaborationPackageActions.onboardExistingClient({ clientId, email, loadRemoteVatIntervalInformation }),
      );
      return;
    }
    store.dispatch(
      CollaborationPackageActions.onboardNonExistingClient({ cloudApiKey, email, loadRemoteVatIntervalInformation }),
    );
  },
});

const onboardingAnnualClientStrategy = (store: Store<AppState>): ActionStrategy => ({
  type: ActionType.ONBOARD_WITH_ANNUAL_CLIENT,
  primaryButtonI18nKey: 'shared.select',
  execute: (config: ActionStrategyParams) => {
    const { cloudApiKey, name, socialSecurityNumber: rawSocialSecurityNumber } = config;
    // removes any dashes from the social security number so we have the date in format of YYYYMMDDXXXX
    const socialSecurityNumber = formatSocialSecurityNumberWithCentry(rawSocialSecurityNumber);

    store.dispatch(
      CollaborationPackageActions.onboardWithAnnualClientCollaborationPackage({
        cloudApiKey,
        name,
        socialSecurityNumber,
      }),
    );
  },
});

const changePackageOnNewSubscriptionStrategy = (store: Store<AppState>): ActionStrategy => ({
  type: ActionType.CHANGE_PACKAGE_ON_NEW_SUBSCRIPTION,
  primaryButtonI18nKey: 'shared.continue',
  execute: (config: ActionStrategyParams) => {
    const { cloudApiKey } = config;

    store.dispatch(CollaborationPackageActions.changePackageOnNewSubscriptionType({ cloudApiKey }));
  },
});

const archiveStrategy = (store: Store<AppState>): ActionStrategy => ({
  type: ActionType.ARCHIVE,
  primaryButtonI18nKey: 'shared.archive',
  execute: (config: ActionStrategyParams) => {
    const { cloudApiKey, name, years, clientAction } = config;
    store.dispatch(CollaborationPackageActions.archiveCloudDatabase({ cloudApiKey, name, years, clientAction }));
  },
});

const unarchiveStrategy = (store: Store<AppState>): ActionStrategy => ({
  type: ActionType.UNARCHIVE,
  primaryButtonI18nKey: 'shared.continue',
  execute: (config: ActionStrategyParams) => {
    const { cloudApiKey, name, socialSecurityNumber: rawSocialSecurityNumber, clientAction } = config;
    // removes any dashes from the social security number so we have the date in format of YYYYMMDDXXXX
    const socialSecurityNumber = formatSocialSecurityNumberWithCentry(rawSocialSecurityNumber);
    store.dispatch(
      CollaborationPackageActions.unarchiveCloudDatabase({ cloudApiKey, name, socialSecurityNumber, clientAction }),
    );
  },
});

const deleteStrategy = (store: Store<AppState>): ActionStrategy => ({
  type: ActionType.DELETE,
  primaryButtonI18nKey: 'shared.delete',
  execute: (config: ActionStrategyParams) => {
    const { cloudApiKey, name, clientAction } = config;
    store.dispatch(CollaborationPackageActions.deleteCloudDatabase({ cloudApiKey, name, clientAction }));
  },
});

export const createStrategyFactory = () => {
  const store = AppInjector.get().get(Store<AppState>);

  const strategyMap = {
    [ActionType.ONBOARD]: onboardingStrategy(store),
    [ActionType.ONBOARD_WITH_ANNUAL_CLIENT]: onboardingAnnualClientStrategy(store),
    [ActionType.CHANGE_PACKAGE_ON_NEW_SUBSCRIPTION]: changePackageOnNewSubscriptionStrategy(store),
    [ActionType.ARCHIVE]: archiveStrategy(store),
    [ActionType.UNARCHIVE]: unarchiveStrategy(store),
    [ActionType.DELETE]: deleteStrategy(store),
    [ActionType.LOADING]: { type: ActionType.LOADING, primaryButtonI18nKey: 'shared.continue', execute: () => {} },
  };

  return (strategyType: ActionType): ActionStrategy => strategyMap[strategyType] ?? strategyMap[ActionType.LOADING];
};
